import { useState } from 'react';
import { type Token, TokenStatus as TokenStatusEnum } from '../../types';
import { TokenStatus } from './TokenStatus';
import { TokenViewModal } from './TokenViewModal';
import { RefreshCw, Trash2, Clock, Eye, LogIn } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { parseUTCDate, isDateExpired } from '../../utils/dateUtils';
import { useTokenDetails } from '../../hooks/useTokens';
import { getProviderLogo } from '../../utils/providerLogos';

interface TokenCardProps {
  token: Token;
  onRefresh: (tokenId: string) => void;
  onDelete: (tokenId: string) => void;
  onReauthorize: (providerId: string) => void;
  isRefreshing?: boolean;
  isDeleting?: boolean;
  isReauthorizing?: boolean;
}

export const TokenCard = ({
  token,
  onRefresh,
  onDelete,
  onReauthorize,
  isRefreshing = false,
  isDeleting = false,
  isReauthorizing = false,
}: TokenCardProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [shouldFetchDetails, setShouldFetchDetails] = useState(false);
  const { data: tokenDetails, isLoading: isLoadingDetails } = useTokenDetails(token.id, shouldFetchDetails);

  const expiresAt = parseUTCDate(token.expiresAt);
  const isExpired = isDateExpired(token.expiresAt);
  const isProviderInactive = token.providerIsActive === false;
  const isTemplateInactive = token.providerTemplateIsActive === false;
  const isProviderUsable = !isProviderInactive && !isTemplateInactive;
  const displayScope = token.scope?.trim() ? token.scope : 'Not set';

  const handleViewTokens = () => {
    setShouldFetchDetails(true);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    // Don't reset shouldFetchDetails to keep the data in cache briefly
  };

  return (
    <div className="card p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center space-x-3 min-w-0">
          {getProviderLogo(token.providerType, 'md')}
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 break-words">{token.providerName}</h3>
            <p className="text-sm text-gray-500 break-words">
              {token.providerType}
              {token.templateName && <span> • {token.templateName}</span>}
            </p>
            {/* External User Information */}
            {(token.externalUserEmail || token.externalUserName) && (
              <p className="text-sm text-gray-600 mt-1 break-all">
                {token.externalUserName && (
                  <span className="font-medium">
                    {token.externalUserName}
                    {token.externalUserFamilyName && ` ${token.externalUserFamilyName}`}
                  </span>
                )}
                {token.externalUserEmail && (
                  <span className={token.externalUserName ? "ml-2" : ""}>
                    ({token.externalUserEmail})
                  </span>
                )}
              </p>
            )}
          </div>
        </div>
        <TokenStatus
          status={token.status}
          expiresInSeconds={token.expiresInSeconds}
          providerIsActive={token.providerIsActive}
          providerTemplateIsActive={token.providerTemplateIsActive}
        />
      </div>

      {/* Token Details */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center text-sm">
          <Clock className="h-4 w-4 mr-2 text-gray-400" />
          <span className="text-gray-600">
            {isExpired ? (
              <span className="text-red-600 font-medium">Expired</span>
            ) : expiresAt ? (
              <>
                Expires{' '}
                <span className="font-medium">
                  {formatDistanceToNow(expiresAt, { addSuffix: true })}
                </span>
              </>
            ) : (
              <span className="text-gray-400">Unknown expiration</span>
            )}
          </span>
        </div>

        {token.lastRefreshAt && (
          <div className="text-sm text-gray-500">
            Last refreshed: {parseUTCDate(token.lastRefreshAt)
              ? formatDistanceToNow(parseUTCDate(token.lastRefreshAt)!, { addSuffix: true })
              : 'Unknown'}
          </div>
        )}

        <div className="text-sm text-gray-500 break-words">
          <span className="font-medium">Scopes:</span> {displayScope}
        </div>
      </div>

      {/* Actions */}
      <div className="grid grid-cols-2 gap-2 pt-4 border-t border-gray-200">
        {token.status === TokenStatusEnum.RequiresReauth ? (
          <button
            onClick={() => onReauthorize(token.providerId)}
            disabled={!isProviderUsable || isReauthorizing || isDeleting}
            className="btn btn-primary col-span-2 w-full min-w-0 px-4 py-2 flex items-center justify-center space-x-2 bg-orange-500 hover:bg-orange-600"
          >
            <LogIn className={`h-4 w-4 ${isReauthorizing ? 'animate-pulse' : ''}`} />
            <span>{isReauthorizing ? 'Redirecting...' : 'Reauthorize'}</span>
          </button>
        ) : (
          <button
            onClick={() => onRefresh(token.id)}
            disabled={!isProviderUsable || isRefreshing || isDeleting}
            className="btn btn-primary col-span-2 w-full min-w-0 px-4 py-2 flex items-center justify-center space-x-2"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span>{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        )}

        <button
          onClick={handleViewTokens}
          disabled={isRefreshing || isDeleting || isReauthorizing}
          className="btn btn-outline w-full min-w-0 px-4 py-2 flex items-center justify-center space-x-2"
        >
          <Eye className="h-4 w-4" />
          <span>View</span>
        </button>

        <button
          onClick={() => onDelete(token.id)}
          disabled={isRefreshing || isDeleting || isReauthorizing}
          className="btn btn-outline w-full min-w-0 px-4 py-2 flex items-center justify-center space-x-2 text-red-600 border-red-300 hover:bg-red-50"
        >
          <Trash2 className="h-4 w-4" />
          <span>{isDeleting ? 'Deleting...' : 'Delete'}</span>
        </button>
      </div>

      {/* Token View Modal */}
      <TokenViewModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        tokenDetails={tokenDetails || null}
        token={token}
        isLoading={isLoadingDetails}
      />
    </div>
  );
};
